import SearchAPI from '../../api/search';
import types from '../mutation-types';

// [PROSPEK] 2026-06-19 Block short message searches before hitting /search/messages
const MINIMUM_MESSAGE_SEARCH_QUERY_LENGTH = 3;
let activeMessageSearchRequestId = 0;
// [/PROSPEK]

const PER_PAGE = 15;

// Paginated search responses can overlap across pages (new records shift
// offsets between fetches), so drop records that are already in the list.
const appendUniqueRecords = (existingRecords, newRecords) => {
  const existingIds = new Set(existingRecords.map(record => record.id));
  return [
    ...existingRecords,
    ...newRecords.filter(record => !existingIds.has(record.id)),
  ];
};
export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  // [PROSPEK] 2026-07-03 Issue #130 — track bounded message search partial-result state
  messageSearchTruncated: false,
  // [/PROSPEK]
  articleRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
    article: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  // [PROSPEK] 2026-07-03 Issue #130 — expose bounded message search partial-result state
  getMessageSearchTruncated(state) {
    return state.messageSearchTruncated;
  },
  // [/PROSPEK]
  getArticleRecords(state) {
    return state.articleRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, payload) {
    const { q, ...filters } = payload;
    if (!q && !Object.keys(filters).length) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q, ...filters }),
        dispatch('conversationSearch', { q, ...filters }),
        dispatch('messageSearch', { q, ...filters }),
        dispatch('articleSearch', { q, ...filters }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ ...searchParams, page });
      commit(types.CONTACT_SEARCH_SET, data.payload.contacts);
      // hasMore uses the raw page size: the records above may shrink on
      // dedupe, so stored counts cannot signal whether more pages exist
      commit(types.CONTACT_SEARCH_SET_UI_FLAG, {
        hasMore: data.payload.contacts.length === PER_PAGE,
      });
      return true;
    } catch (error) {
      // Failure is reported so callers can roll back their page counter
      return false;
    } finally {
      commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async conversationSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({ ...searchParams, page });
      commit(types.CONVERSATION_SEARCH_SET, data.payload.conversations);
      commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, {
        hasMore: data.payload.conversations.length === PER_PAGE,
      });
      return true;
    } catch (error) {
      // Failure is reported so callers can roll back their page counter
      return false;
    } finally {
      commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async messageSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    // [PROSPEK] 2026-06-19 Block short message searches before hitting /search/messages
    const normalizedQuery = searchParams.q?.trim() || '';
    activeMessageSearchRequestId += 1;
    const requestId = activeMessageSearchRequestId;
    // [/PROSPEK]
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    // [PROSPEK] 2026-07-03 Issue #130 — drop stale truncation notice before a new search resolves or fails
    commit(types.MESSAGE_SEARCH_TRUNCATED_SET, false);
    // [/PROSPEK]
    try {
      // [PROSPEK] 2026-06-19 Block short message searches before hitting /search/messages
      if (normalizedQuery.length < MINIMUM_MESSAGE_SEARCH_QUERY_LENGTH) {
        commit(types.MESSAGE_SEARCH_CLEAR);
        return true;
      }
      // [/PROSPEK]

      const { data } = await SearchAPI.messages({ ...searchParams, page });
      // [PROSPEK] 2026-06-19 Ignore stale responses after a newer short query clears results
      if (requestId !== activeMessageSearchRequestId) return true;
      // [/PROSPEK]

      commit(types.MESSAGE_SEARCH_SET, data.payload.messages);
      // [PROSPEK] 2026-07-03 Issue #130 — capture bounded message search partial-result state
      commit(
        types.MESSAGE_SEARCH_TRUNCATED_SET,
        Boolean(data.payload.search_truncated)
      );
      // [/PROSPEK]
      commit(types.MESSAGE_SEARCH_SET_UI_FLAG, {
        hasMore: data.payload.messages.length === PER_PAGE,
      });
      return true;
    } catch (error) {
      // Failure is reported so callers can roll back their page counter
      return false;
    } finally {
      // [PROSPEK] 2026-06-19 Ignore stale responses after a newer short query clears results
      if (requestId === activeMessageSearchRequestId) {
        commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
      // [/PROSPEK]
    }
  },
  async articleSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.articles({ ...searchParams, page });
      commit(types.ARTICLE_SEARCH_SET, data.payload.articles);
      commit(types.ARTICLE_SEARCH_SET_UI_FLAG, {
        hasMore: data.payload.articles.length === PER_PAGE,
      });
      return true;
    } catch (error) {
      // Failure is reported so callers can roll back their page counter
      return false;
    } finally {
      commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async clearSearchResults({ commit }) {
    commit(types.CLEAR_SEARCH_RESULTS);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = appendUniqueRecords(state.contactRecords, records);
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = appendUniqueRecords(
      state.conversationRecords,
      records
    );
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = appendUniqueRecords(state.messageRecords, records);
  },
  // [PROSPEK] 2026-07-03 Issue #130 — store bounded message search partial-result state
  [types.MESSAGE_SEARCH_TRUNCATED_SET](state, searchTruncated) {
    state.messageSearchTruncated = searchTruncated;
  },
  // [/PROSPEK]
  // [PROSPEK] 2026-06-19 Clear stale message results when search is too short
  // 2026-07-03 Issue #130 — clear stale bounded-search notice
  [types.MESSAGE_SEARCH_CLEAR](state) {
    state.messageRecords = [];
    state.messageSearchTruncated = false;
  },
  // [/PROSPEK]
  [types.ARTICLE_SEARCH_SET](state, records) {
    state.articleRecords = appendUniqueRecords(state.articleRecords, records);
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
  [types.ARTICLE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.article = { ...state.uiFlags.article, ...uiFlags };
  },
  [types.CLEAR_SEARCH_RESULTS](state) {
    state.contactRecords = [];
    state.conversationRecords = [];
    state.messageRecords = [];
    // [PROSPEK] 2026-07-03 Issue #130 — clear stale bounded-search notice with search results
    state.messageSearchTruncated = false;
    // [/PROSPEK]
    state.articleRecords = [];
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
